import { createContext, PropsWithChildren, useContext } from 'react';
import orderBy from 'lodash/orderBy';

export interface WholeDiagram<T extends HotSpotItem> {
   diagramid: string | number;
   title: string;
   imageUrl: string;
   thumbnailUrl: string;
   width: number;
   height: number;
   data: HotSpot<T>[];
}

export interface HotSpot<T extends HotSpotItem> {
   id: string;
   x: number;
   y: number;
   scaledX?: number;
   scaledY?: number;
   tabIndex?: number;
   radius: number | null;
   items: T[];
}

export interface HotSpotItem {
   id: number;
   datapointid: string;
}

type DataContextType<T extends HotSpotItem> = {
   data: HotSpot<T>[];
};

export const useDataContext = <T extends HotSpotItem>() => {
   const context = useContext(DataContext);
   return context as DataContextType<T>;
};

const DataContext = createContext({
   data: [] as HotSpot<HotSpotItem>[],
});

function setTabIndexSpatially<T extends HotSpotItem>(data: HotSpot<T>[]): HotSpot<T>[] {
   const orderData = orderBy(
      data,
      [datapoint => datapoint.y, datapoint => datapoint.x],
      ['asc', 'asc']
   );

   let tabIndex = 1;

   const tabIndexedData = orderData.map(datapoint => ({ ...datapoint, tabIndex: tabIndex++ }));

   return tabIndexedData;
}

export type RequiredDataContextProps<T extends HotSpotItem> = {
   data: HotSpot<T>[];
};

export function DataContextProvider<T extends HotSpotItem>({
   children,
   data,
}: PropsWithChildren<RequiredDataContextProps<T>>) {
   const dataContext = {
      data: setTabIndexSpatially(data),
   };

   return <DataContext.Provider value={dataContext}>{children}</DataContext.Provider>;
}
